import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminRepository from '../../services/AdminRepository.js';
import SortOrderToggle from '../common/SortOrderToggle.jsx';
import { formatBytes, formatDateKey, formatNumber } from '../../utils/statisticsFormat.js';
import {
  applyDrilldownFilter,
  buildToolbarApiParams,
  drilldownStateToQuery,
  DRILLDOWN_STATE_INITIAL,
  sortAdminHubRows,
} from '../../utils/adminHubDetailsClientFilter.js';
import './StatisticsAdminHubDetailPanel.css';
import './StatisticsDetailShared.css';

const DOMAIN_CONFIG = {
  clinics: {
    label: 'クリニック',
    description: 'クリニック単位の属性・ライセンス・利用量',
    defaultAggregationView: 'demo',
    aggregationViews: [
      { id: 'demo', label: 'デモ区分' },
      { id: 'payment', label: '決済登録' },
      { id: 'licenseUsage', label: 'ライセンス利用' },
      { id: 'usage', label: '利用量' },
    ],
    sortOptions: [
      { value: 'name', label: '名前' },
      { value: 'userTotal', label: 'ユーザー数' },
      { value: 'licenseUsed', label: 'ライセンス数' },
      { value: 'monthlyFeeTotal', label: '月額合計（税別）' },
      { value: 'patients', label: '登録患者' },
      { value: 'records', label: 'レコード' },
      { value: 'storageBytes', label: 'Storage' },
      { value: 'updates', label: '更新数' },
    ],
  },
  users: {
    label: 'ユーザー',
    description: 'ユーザー一覧とロール別集計',
    defaultAggregationView: 'byRole',
    aggregationViews: [
      { id: 'byRole', label: 'ロール別' },
    ],
    sortOptions: [
      { value: 'displayName', label: '名前' },
      { value: 'email', label: 'メール' },
      { value: 'role', label: 'ロール' },
      { value: 'clinicName', label: 'クリニック' },
      { value: 'isActive', label: '有効状態' },
    ],
  },
  contracts: {
    label: '契約',
    description: 'ライセンス契約のステータス・製品・利用料',
    defaultAggregationView: 'byStatus',
    aggregationViews: [
      { id: 'byStatus', label: 'ステータス別' },
      { id: 'byLicense', label: 'ライセンス別' },
      { id: 'byPrice', label: '利用料別' },
    ],
    sortOptions: [
      { value: 'displayName', label: '製品名' },
      { value: 'licenseName', label: 'ライセンス' },
      { value: 'clinicName', label: 'クリニック' },
      { value: 'status', label: 'ステータス' },
      { value: 'monthlyPrice', label: '月額（税別）' },
    ],
  },
};

function formatCurrency(value) {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount) || amount <= 0) return '¥0';
  return `¥${Math.round(amount).toLocaleString('ja-JP')}`;
}

function resolveGroupFilterPatch(domain, aggregationView, groupKey) {
  if (domain === 'clinics') {
    if (aggregationView === 'demo') {
      return { demoFilter: groupKey === 'demo' ? 'demo' : 'non-demo' };
    }
    if (aggregationView === 'payment') {
      return { paymentFilter: groupKey === 'registered' ? 'registered' : 'unregistered' };
    }
    if (aggregationView === 'licenseUsage') {
      return { licenseUsageBand: groupKey };
    }
    if (aggregationView === 'usage') {
      return { usagePatientsBand: groupKey };
    }
  }
  if (domain === 'users' && aggregationView === 'byRole') {
    return { roleFilter: groupKey };
  }
  if (domain === 'contracts') {
    if (aggregationView === 'byStatus') {
      return { statusFilter: groupKey };
    }
    if (aggregationView === 'byLicense') {
      return { licenseFilter: groupKey };
    }
    if (aggregationView === 'byPrice') {
      if (groupKey === 'free') return { priceBand: 'free', priceRangeBand: 'all' };
      return { priceBand: 'all', priceRangeBand: groupKey };
    }
  }
  return {};
}

function resolveSummaryFilterPatch(domain, filterId, value) {
  if (domain === 'clinics') {
    const map = {
      demo: { demoFilter: 'demo' },
      nonDemo: { demoFilter: 'non-demo' },
      paymentRegistered: { paymentFilter: 'registered' },
      stripeLinked: { stripeFilter: 'linked' },
      expiredLicense: { expiredLicenseFilter: 'yes' },
    };
    return map[filterId] || {};
  }
  if (domain === 'users') {
    const map = {
      active: { userStatusFilter: 'active' },
      inactive: { userStatusFilter: 'inactive' },
      role: { roleFilter: value },
    };
    return map[filterId] || {};
  }
  if (domain === 'contracts') {
    const map = {
      stripeSubscription: { stripeSubscriptionFilter: 'yes' },
      status: { statusFilter: value },
    };
    return map[filterId] || {};
  }
  return {};
}

function HubSummaryCards({ domain, metrics, licenses, activeSummaryKey, onSummaryClick }) {
  if (!metrics) return null;

  const cardClass = (key) => (
    `stats-metric-card stats-metric-card--filterable${activeSummaryKey === key ? ' is-active' : ''}`
  );

  const FilterCard = ({ filterKey, filterValue, children, ariaLabel }) => (
    <button
      type="button"
      className={cardClass(filterValue ? `${filterKey}:${filterValue}` : filterKey)}
      onClick={() => onSummaryClick(filterKey, filterValue)}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );

  if (domain === 'clinics') {
    const clinicMetrics = metrics.clinics || {};
    return (
      <div className="stats-detail-summary-grid">
        <div className="stats-metric-card">
          <span>クリニック総数</span>
          <strong>{formatNumber(clinicMetrics.total)}</strong>
        </div>
        <FilterCard filterKey="demo" ariaLabel="デモクリニックで絞り込み">
          <span>デモ</span>
          <strong>{formatNumber(clinicMetrics.demo)}</strong>
        </FilterCard>
        <FilterCard filterKey="nonDemo" ariaLabel="通常クリニックで絞り込み">
          <span>通常</span>
          <strong>{formatNumber(clinicMetrics.nonDemo)}</strong>
        </FilterCard>
        <FilterCard filterKey="paymentRegistered" ariaLabel="決済登録済で絞り込み">
          <span>決済登録</span>
          <strong>{formatNumber(clinicMetrics.paymentInfoRegistered)}</strong>
        </FilterCard>
        <FilterCard filterKey="stripeLinked" ariaLabel="Stripe連携済で絞り込み">
          <span>Stripe連携</span>
          <strong>{formatNumber(clinicMetrics.stripeCustomerLinked)}</strong>
        </FilterCard>
        <FilterCard filterKey="expiredLicense" ariaLabel="ライセンス期限切れで絞り込み">
          <span>ライセンス期限切れ</span>
          <strong>{formatNumber(clinicMetrics.hasExpiredLicense)}</strong>
        </FilterCard>
        <div className="stats-metric-card">
          <span>ライセンス使用合計</span>
          <strong>{formatNumber(licenses?.usedTotal)}</strong>
          {licenses?.configuredLimit != null && (
            <small>上限: {formatNumber(licenses.configuredLimit)}</small>
          )}
        </div>
      </div>
    );
  }

  if (domain === 'users') {
    const userMetrics = metrics.users || {};
    const byRole = userMetrics.byRole || {};
    const roleEntries = Object.entries(byRole).sort((a, b) => b[1] - a[1]);

    return (
      <>
        <div className="stats-detail-summary-grid">
          <div className="stats-metric-card">
            <span>ユーザー総数</span>
            <strong>{formatNumber(userMetrics.total)}</strong>
          </div>
          <FilterCard filterKey="active" ariaLabel="有効ユーザーのみ表示">
            <span>有効</span>
            <strong>{formatNumber(userMetrics.active)}</strong>
          </FilterCard>
          <FilterCard filterKey="inactive" ariaLabel="無効ユーザーのみ表示">
            <span>無効</span>
            <strong>{formatNumber(userMetrics.inactive)}</strong>
          </FilterCard>
        </div>
        {roleEntries.length > 0 && (
          <div className="stats-summary-chip-row">
            {roleEntries.map(([role, count]) => (
              <button
                key={role}
                type="button"
                className={`stats-summary-chip${activeSummaryKey === `role:${role}` ? ' is-active' : ''}`}
                onClick={() => onSummaryClick('role', role)}
              >
                {role}: {formatNumber(count)}
              </button>
            ))}
          </div>
        )}
      </>
    );
  }

  const contractMetrics = metrics.contracts || {};
  const byStatus = contractMetrics.byStatus || {};
  const statusEntries = Object.entries(byStatus).sort((a, b) => b[1] - a[1]);

  return (
    <>
      <div className="stats-detail-summary-grid">
        <div className="stats-metric-card">
          <span>契約総数</span>
          <strong>{formatNumber(contractMetrics.total)}</strong>
        </div>
        <FilterCard filterKey="stripeSubscription" ariaLabel="Stripe Subscription 連携で絞り込み">
          <span>Stripe Subscription</span>
          <strong>{formatNumber(contractMetrics.withStripeSubscription)}</strong>
        </FilterCard>
      </div>
      {statusEntries.length > 0 && (
        <div className="stats-summary-chip-row">
          {statusEntries.map(([status, count]) => (
            <button
              key={status}
              type="button"
              className={`stats-summary-chip${activeSummaryKey === `status:${status}` ? ' is-active' : ''}`}
              onClick={() => onSummaryClick('status', status)}
            >
              {status}: {formatNumber(count)}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function computeGroupTotals(groups, domain) {
  return (groups || []).reduce((acc, group) => {
    acc.count += Number(group.count || 0);
    acc.userTotal += Number(group.userTotal || 0);
    acc.licenseUsed += Number(group.licenseUsed || 0);
    acc.patients += Number(group.patients || 0);
    acc.storageBytes += Number(group.storageBytes || 0);
    acc.activeUsers += Number(group.activeUsers || 0);
    acc.inactiveUsers += Number(group.inactiveUsers || 0);
    if (domain === 'clinics') {
      acc.monthlyFeeTrialTotal += Number(group.monthlyFeeTrialTotal || 0);
      acc.monthlyFeeBillableTotal += Number(group.monthlyFeeBillableTotal || 0);
    }
    if (domain === 'contracts') {
      acc.monthlyPriceTrialTotal += Number(group.monthlyPriceTrialTotal || 0);
      acc.monthlyPriceBillableTotal += Number(group.monthlyPriceBillableTotal || 0);
    }
    return acc;
  }, {
    count: 0,
    userTotal: 0,
    licenseUsed: 0,
    patients: 0,
    storageBytes: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    monthlyFeeTrialTotal: 0,
    monthlyFeeBillableTotal: 0,
    monthlyPriceTrialTotal: 0,
    monthlyPriceBillableTotal: 0,
  });
}

function GroupTable({ groups, domain, activeGroupKey, onGroupClick }) {
  if (!groups?.length) {
    return <div className="stats-empty">集計データがありません。</div>;
  }

  const totals = computeGroupTotals(groups, domain);
  const showClinicFees = domain === 'clinics';
  const showContractFees = domain === 'contracts';

  return (
    <div className="stats-table-wrap stats-table-wrap--flat">
      <table className="stats-table stats-table--group">
        <thead>
          <tr>
            <th>区分</th>
            <th>件数</th>
            {domain === 'clinics' && (
              <>
                <th>ユーザー</th>
                <th>ライセンス</th>
                <th>試用中（未課金）</th>
                <th>課金中</th>
                <th>月額合計（税別）</th>
                <th>登録患者</th>
                <th>Storage</th>
              </>
            )}
            {domain === 'users' && (
              <>
                <th>有効</th>
                <th>無効</th>
              </>
            )}
            {domain === 'contracts' && (
              <>
                <th>試用中（未課金）</th>
                <th>課金中</th>
                <th>月額合計（税別）</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <tr
              key={group.key}
              className={`stats-group-row${activeGroupKey === group.key ? ' is-active' : ''}`}
              onClick={() => onGroupClick?.(group.key)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onGroupClick?.(group.key);
                }
              }}
              tabIndex={0}
              role="button"
              aria-pressed={activeGroupKey === group.key}
            >
              <td><strong>{group.label}</strong></td>
              <td>{formatNumber(group.count)}</td>
              {domain === 'clinics' && (
                <>
                  <td>{formatNumber(group.userTotal)}</td>
                  <td>{formatNumber(group.licenseUsed)}</td>
                  <td>{formatCurrency(group.monthlyFeeTrialTotal)}</td>
                  <td>{formatCurrency(group.monthlyFeeBillableTotal)}</td>
                  <td>{formatCurrency(Number(group.monthlyFeeTrialTotal || 0) + Number(group.monthlyFeeBillableTotal || 0))}</td>
                  <td>{formatNumber(group.patients)}</td>
                  <td>{formatBytes(group.storageBytes)}</td>
                </>
              )}
              {domain === 'users' && (
                <>
                  <td>{formatNumber(group.activeUsers)}</td>
                  <td>{formatNumber(group.inactiveUsers)}</td>
                </>
              )}
              {domain === 'contracts' && (
                <>
                  <td>{formatCurrency(group.monthlyPriceTrialTotal)}</td>
                  <td>{formatCurrency(group.monthlyPriceBillableTotal)}</td>
                  <td>{formatCurrency(Number(group.monthlyPriceTrialTotal || 0) + Number(group.monthlyPriceBillableTotal || 0))}</td>
                </>
              )}
            </tr>
          ))}
          {(showClinicFees || showContractFees) && (
            <tr className="stats-group-total-row">
              <td><strong>合計</strong></td>
              <td>{formatNumber(totals.count)}</td>
              {domain === 'clinics' && (
                <>
                  <td>{formatNumber(totals.userTotal)}</td>
                  <td>{formatNumber(totals.licenseUsed)}</td>
                  <td>{formatCurrency(totals.monthlyFeeTrialTotal)}</td>
                  <td>{formatCurrency(totals.monthlyFeeBillableTotal)}</td>
                  <td>{formatCurrency(totals.monthlyFeeTrialTotal + totals.monthlyFeeBillableTotal)}</td>
                  <td>{formatNumber(totals.patients)}</td>
                  <td>{formatBytes(totals.storageBytes)}</td>
                </>
              )}
              {domain === 'contracts' && (
                <>
                  <td>{formatCurrency(totals.monthlyPriceTrialTotal)}</td>
                  <td>{formatCurrency(totals.monthlyPriceBillableTotal)}</td>
                  <td>{formatCurrency(totals.monthlyPriceTrialTotal + totals.monthlyPriceBillableTotal)}</td>
                </>
              )}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function contractRowKey(row) {
  return `${row.clinicId || 'unknown'}::${row.id || row.licenseName || 'unknown'}`;
}

function DetailRowsTable({ rows, domain }) {
  if (!rows?.length) {
    return <div className="stats-empty">条件に一致するデータがありません。</div>;
  }

  if (domain === 'users') {
    return (
      <div className="stats-table-wrap stats-table-wrap--flat">
        <table className="stats-table">
          <thead>
            <tr>
              <th>名前</th>
              <th>メール</th>
              <th>ロール</th>
              <th>クリニック</th>
              <th>状態</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.uid}>
                <td>{row.displayName || '—'}</td>
                <td>{row.email || '—'}</td>
                <td>{row.role}</td>
                <td>
                  <strong>{row.clinicName || row.clinicId}</strong>
                  <small>{row.clinicId}</small>
                </td>
                <td className="stats-table__status">
                  <span className={`stats-status-badge${row.isActive === false ? ' is-inactive' : ' is-active'}`}>
                    {row.isActive === false ? '無効' : '有効'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (domain === 'contracts') {
    const totals = rows.reduce((acc, row) => {
      acc.trial += Number(row.monthlyPriceTrial || 0);
      acc.billable += Number(row.monthlyPriceBillable || 0);
      return acc;
    }, { trial: 0, billable: 0 });

    return (
      <div className="stats-table-wrap stats-table-wrap--flat">
        <table className="stats-table stats-table--contract-rows">
          <thead>
            <tr>
              <th>製品</th>
              <th>ライセンス</th>
              <th>クリニック</th>
              <th>ステータス</th>
              <th>試用中（未課金）</th>
              <th>課金中</th>
              <th>合計</th>
              <th>Stripe</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={contractRowKey(row)}>
                <td>{row.displayName || row.licenseName}</td>
                <td>{row.licenseName}</td>
                <td>
                  <strong>{row.clinicName}</strong>
                  <small>{row.clinicId}</small>
                </td>
                <td>{row.status}</td>
                <td>{row.isFree ? '—' : formatCurrency(row.monthlyPriceTrial)}</td>
                <td>{row.isFree ? '—' : formatCurrency(row.monthlyPriceBillable)}</td>
                <td>
                  {row.isFree ? '無料' : formatCurrency(Number(row.monthlyPriceTrial || 0) + Number(row.monthlyPriceBillable || 0))}
                </td>
                <td className="stats-table__status">
                  {row.hasStripeSubscription ? (
                    <span className="stats-status-badge is-active">連携</span>
                  ) : (
                    <span className="stats-status-badge is-inactive">—</span>
                  )}
                </td>
              </tr>
            ))}
            <tr className="stats-group-total-row">
              <td colSpan="4"><strong>合計</strong></td>
              <td>{formatCurrency(totals.trial)}</td>
              <td>{formatCurrency(totals.billable)}</td>
              <td>{formatCurrency(totals.trial + totals.billable)}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  const totals = rows.reduce((acc, row) => {
    acc.trial += Number(row.monthlyFeeTrialTotal || 0);
    acc.billable += Number(row.monthlyFeeBillableTotal || 0);
    return acc;
  }, { trial: 0, billable: 0 });

  return (
    <div className="stats-table-wrap stats-table-wrap--flat">
      <table className="stats-table stats-table--clinic-rows">
        <thead>
          <tr>
            <th>クリニック</th>
            <th>区分</th>
            <th>ユーザー</th>
            <th>ライセンス</th>
            <th>試用中（未課金）</th>
            <th>課金中</th>
            <th>月額合計（税別）</th>
            <th>登録患者</th>
            <th>新規患者</th>
            <th>Storage</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.clinicId}>
              <td>
                <strong>{row.name}</strong>
                <small>{row.clinicId}</small>
              </td>
              <td>
                {row.isDemo ? 'デモ' : '通常'}
                {row.hasPaymentInfo ? ' / 決済済' : ' / 決済未'}
              </td>
              <td>{formatNumber(row.userTotal)}</td>
              <td>{formatNumber(row.licenseUsed)}</td>
              <td>{formatCurrency(row.monthlyFeeTrialTotal)}</td>
              <td>{formatCurrency(row.monthlyFeeBillableTotal)}</td>
              <td>{formatCurrency(Number(row.monthlyFeeTrialTotal || 0) + Number(row.monthlyFeeBillableTotal || 0))}</td>
              <td>{formatNumber(row.usage?.patients)}</td>
              <td>{formatNumber(row.usage?.newPatients)}</td>
              <td>{formatBytes(row.usage?.storageBytes)}</td>
            </tr>
          ))}
          <tr className="stats-group-total-row">
            <td colSpan="4"><strong>合計</strong></td>
            <td>{formatCurrency(totals.trial)}</td>
            <td>{formatCurrency(totals.billable)}</td>
            <td>{formatCurrency(totals.trial + totals.billable)}</td>
            <td colSpan="3" />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const TOOLBAR_FILTER_INITIAL = {
  demoFilter: 'all',
  paymentFilter: 'all',
  roleFilter: 'all',
  statusFilter: 'all',
  licenseFilter: 'all',
  priceBand: 'all',
  priceRangeBand: 'all',
  userStatusFilter: 'all',
};

const StatisticsAdminHubDetailPanel = ({ domain, targetDateKey, hubMetrics, onBack }) => {
  const repo = useMemo(() => AdminRepository.getInstance(), []);
  const config = DOMAIN_CONFIG[domain];
  const [aggregationView, setAggregationView] = useState(config?.defaultAggregationView || 'demo');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(config?.sortOptions?.[0]?.value || 'name');
  const [sortOrder, setSortOrder] = useState('desc');
  const [toolbarFilters, setToolbarFilters] = useState({ ...TOOLBAR_FILTER_INITIAL });
  const [drilldownFilters, setDrilldownFilters] = useState({ ...DRILLDOWN_STATE_INITIAL });
  const [minUsers, setMinUsers] = useState('');
  const [minLicenses, setMinLicenses] = useState('');
  const [minPatients, setMinPatients] = useState('');
  const [activeGroupKey, setActiveGroupKey] = useState(null);
  const [activeSummaryKey, setActiveSummaryKey] = useState(null);
  const [summaryGroups, setSummaryGroups] = useState([]);
  const [summaryCounts, setSummaryCounts] = useState({ total: 0, filtered: 0 });
  const [baseRows, setBaseRows] = useState([]);
  const [baseCounts, setBaseCounts] = useState({ total: 0, filtered: 0 });
  const [facets, setFacets] = useState({});
  const [resolvedDateKey, setResolvedDateKey] = useState(targetDateKey);
  const [sourceNote, setSourceNote] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [baseLoading, setBaseLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setAggregationView(config?.defaultAggregationView || 'demo');
    setSortKey(config?.sortOptions?.[0]?.value || 'name');
    setSortOrder(domain === 'clinics' ? 'asc' : 'desc');
    setSearch('');
    setToolbarFilters({ ...TOOLBAR_FILTER_INITIAL });
    setDrilldownFilters({ ...DRILLDOWN_STATE_INITIAL });
    setMinUsers('');
    setMinLicenses('');
    setMinPatients('');
    setActiveGroupKey(null);
    setActiveSummaryKey(null);
  }, [domain, config]);

  useEffect(() => {
    setActiveGroupKey(null);
    setDrilldownFilters({ ...DRILLDOWN_STATE_INITIAL });
  }, [aggregationView]);

  const toolbarApiParams = useMemo(() => buildToolbarApiParams({
    domain,
    targetDateKey,
    search,
    toolbarFilters,
    minUsers,
    minLicenses,
    minPatients,
  }), [domain, targetDateKey, search, toolbarFilters, minUsers, minLicenses, minPatients]);

  const loadSummary = useCallback(async () => {
    if (!domain) return;
    setSummaryLoading(true);
    setError('');
    try {
      const response = await repo.getAdminHubStatisticsDetails({
        ...toolbarApiParams,
        view: aggregationView,
      });
      setSummaryGroups(response?.result?.groups || []);
      setSummaryCounts(response?.result?.counts || { total: 0, filtered: 0 });
      setFacets((prev) => ({ ...prev, ...(response?.facets || {}) }));
      setResolvedDateKey(response?.targetDateKey || targetDateKey);
      if (response?.source === 'rebuilt') setSourceNote('（スナップショット再構築）');
    } catch (err) {
      setError(err.message || '集計データの取得に失敗しました');
    } finally {
      setSummaryLoading(false);
    }
  }, [repo, domain, toolbarApiParams, aggregationView, targetDateKey]);

  const loadBaseRows = useCallback(async () => {
    if (!domain) return;
    setBaseLoading(true);
    setError('');
    try {
      const response = await repo.getAdminHubStatisticsDetails({
        ...toolbarApiParams,
        view: 'list',
        sort: 'name',
        order: 'asc',
      });
      setBaseRows(response?.result?.rows || []);
      setBaseCounts(response?.result?.counts || { total: 0, filtered: 0 });
      setFacets((prev) => ({ ...prev, ...(response?.facets || {}) }));
      setResolvedDateKey(response?.targetDateKey || targetDateKey);
      if (response?.source === 'rebuilt') setSourceNote('（スナップショット再構築）');
    } catch (err) {
      setError(err.message || '一覧データの取得に失敗しました');
    } finally {
      setBaseLoading(false);
    }
  }, [repo, domain, toolbarApiParams, targetDateKey]);

  useEffect(() => {
    const timer = setTimeout(loadSummary, 200);
    return () => clearTimeout(timer);
  }, [loadSummary]);

  useEffect(() => {
    const timer = setTimeout(loadBaseRows, 200);
    return () => clearTimeout(timer);
  }, [loadBaseRows]);

  const drilldownQuery = useMemo(
    () => drilldownStateToQuery(domain, drilldownFilters),
    [domain, drilldownFilters]
  );

  const displayRows = useMemo(() => {
    const filtered = applyDrilldownFilter(domain, baseRows, drilldownQuery);
    return sortAdminHubRows(filtered, domain, sortKey, sortOrder);
  }, [domain, baseRows, drilldownQuery, sortKey, sortOrder]);

  const scrollToList = useCallback(() => {
    document.getElementById('stats-admin-hub-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleGroupClick = useCallback((groupKey) => {
    if (activeGroupKey === groupKey) {
      setDrilldownFilters({ ...DRILLDOWN_STATE_INITIAL });
      setActiveGroupKey(null);
    } else {
      setDrilldownFilters({ ...DRILLDOWN_STATE_INITIAL, ...resolveGroupFilterPatch(domain, aggregationView, groupKey) });
      setActiveGroupKey(groupKey);
      setActiveSummaryKey(null);
    }
    scrollToList();
  }, [activeGroupKey, aggregationView, domain, scrollToList]);

  const handleSummaryClick = useCallback((filterId, value) => {
    const summaryKey = value ? `${filterId}:${value}` : filterId;
    if (activeSummaryKey === summaryKey) {
      setDrilldownFilters({ ...DRILLDOWN_STATE_INITIAL });
      setActiveSummaryKey(null);
    } else {
      setDrilldownFilters({ ...DRILLDOWN_STATE_INITIAL, ...resolveSummaryFilterPatch(domain, filterId, value) });
      setActiveSummaryKey(summaryKey);
      setActiveGroupKey(null);
    }
    scrollToList();
  }, [activeSummaryKey, domain, scrollToList]);

  const handleToolbarFilterChange = useCallback((patch) => {
    setToolbarFilters((prev) => ({ ...prev, ...patch }));
    setDrilldownFilters({ ...DRILLDOWN_STATE_INITIAL });
    setActiveGroupKey(null);
    setActiveSummaryKey(null);
  }, []);

  if (!config) return null;

  const aggregationLabel = config.aggregationViews.find((item) => item.id === aggregationView)?.label || '集計';

  return (
    <div className="stats-admin-hub-detail-page">
      <header className="stats-admin-hub-page-header">
        <button type="button" className="stats-detail-back" onClick={onBack}>
          ← 統計サマリーに戻る
        </button>
        <h1>{config.label}</h1>
        <p className="stats-detail-meta">
          {config.description} / 対象日 {formatDateKey(resolvedDateKey || targetDateKey)}
          {sourceNote}
        </p>
      </header>

      {error && <div className="stats-alert danger stats-detail-alert">{error}</div>}

      <section className="stats-admin-hub-summary-block">
        <div className="stats-admin-hub-block-header">
          <h2>サマリー</h2>
          <p>各項目をクリックすると、下の一覧をその条件で絞り込みます。</p>
        </div>
        <HubSummaryCards
          domain={domain}
          metrics={hubMetrics}
          licenses={hubMetrics?.licenses}
          activeSummaryKey={activeSummaryKey}
          onSummaryClick={handleSummaryClick}
        />
      </section>

      <section className="stats-admin-hub-summary-block">
        <div className="stats-admin-hub-block-header">
          <h2>集計</h2>
          <p>
            {aggregationLabel} の内訳（{formatNumber(summaryCounts.filtered)} / {formatNumber(summaryCounts.total)} 件）。
            行をクリックすると一覧のみ絞り込みます（表は常に全区分を表示）。
          </p>
        </div>

        {config.aggregationViews.length > 1 && (
          <div className="stats-admin-hub-tabs">
            {config.aggregationViews.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`stats-admin-hub-tab${aggregationView === item.id ? ' is-active' : ''}`}
                onClick={() => setAggregationView(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}

        {summaryLoading ? (
          <div className="stats-empty">集計データを読み込んでいます...</div>
        ) : (
          <GroupTable
            groups={summaryGroups}
            domain={domain}
            activeGroupKey={activeGroupKey}
            onGroupClick={handleGroupClick}
          />
        )}
      </section>

      <section className="stats-admin-hub-list-block" id="stats-admin-hub-list">
        <div className="stats-admin-hub-block-header">
          <h2>一覧</h2>
          <p>
            {formatNumber(displayRows.length)} / {formatNumber(baseCounts.filtered)} 件
            {activeGroupKey || activeSummaryKey ? '（絞り込み中）' : ''}
          </p>
        </div>

        <div className="list-toolbar stats-detail-toolbar stats-detail-toolbar--flat">
          <div className="search-bar list-toolbar__search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="検索..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {domain === 'clinics' && (
            <>
              <select className="admin-select" value={toolbarFilters.demoFilter} onChange={(e) => handleToolbarFilterChange({ demoFilter: e.target.value })}>
                <option value="all">デモ: すべて</option>
                <option value="demo">デモのみ</option>
                <option value="non-demo">デモ以外</option>
              </select>
              <select className="admin-select" value={toolbarFilters.paymentFilter} onChange={(e) => handleToolbarFilterChange({ paymentFilter: e.target.value })}>
                <option value="all">決済: すべて</option>
                <option value="registered">登録済</option>
                <option value="unregistered">未登録</option>
              </select>
              <input type="number" min="0" className="stats-filter-input" placeholder="ユーザー下限" value={minUsers} onChange={(e) => setMinUsers(e.target.value)} />
              <input type="number" min="0" className="stats-filter-input" placeholder="ライセンス下限" value={minLicenses} onChange={(e) => setMinLicenses(e.target.value)} />
              <input type="number" min="0" className="stats-filter-input" placeholder="患者数下限" value={minPatients} onChange={(e) => setMinPatients(e.target.value)} />
            </>
          )}

          {domain === 'users' && (
            <>
              <select className="admin-select" value={toolbarFilters.roleFilter} onChange={(e) => handleToolbarFilterChange({ roleFilter: e.target.value })}>
                <option value="all">ロール: すべて</option>
                {(facets.roles || []).map((role) => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
              <select className="admin-select" value={toolbarFilters.userStatusFilter} onChange={(e) => handleToolbarFilterChange({ userStatusFilter: e.target.value })}>
                <option value="all">状態: すべて</option>
                <option value="active">有効のみ</option>
                <option value="inactive">無効のみ</option>
              </select>
            </>
          )}

          {domain === 'contracts' && (
            <>
              <select className="admin-select" value={toolbarFilters.statusFilter} onChange={(e) => handleToolbarFilterChange({ statusFilter: e.target.value })}>
                <option value="all">ステータス: すべて</option>
                {(facets.contractStatuses || []).map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <select className="admin-select" value={toolbarFilters.licenseFilter} onChange={(e) => handleToolbarFilterChange({ licenseFilter: e.target.value })}>
                <option value="all">ライセンス: すべて</option>
                {(facets.licenseNames || []).map((licenseName) => (
                  <option key={licenseName} value={licenseName}>{licenseName}</option>
                ))}
              </select>
              <select className="admin-select" value={toolbarFilters.priceBand} onChange={(e) => handleToolbarFilterChange({ priceBand: e.target.value })}>
                <option value="all">料金: すべて</option>
                <option value="free">無料</option>
                <option value="paid">有料</option>
              </select>
            </>
          )}

          <div className="list-toolbar__sort">
            <span className="list-toolbar__sort-label">並び替え</span>
            <select className="admin-select" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
              {config.sortOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <SortOrderToggle
              order={sortOrder}
              onToggle={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
            />
          </div>
        </div>

        {baseLoading ? (
          <div className="stats-empty">一覧データを読み込んでいます...</div>
        ) : (
          <DetailRowsTable rows={displayRows} domain={domain} />
        )}
      </section>
    </div>
  );
};

export default StatisticsAdminHubDetailPanel;
